/**
 * The town's enchanting helper (`HubView.enchanting`), opened by the Enchanting button above the vault or in the
 * Enchanter's Emporium. It drills down: your gear (equipped first), then a piece's prefix or suffix (what fills it,
 * the Emporium's raise and extract), then every enchant scroll in your pack and vault that fits that slot, with its
 * check and the ranks it must bridge, then the scroll itself: the Enchant dialog's body (`enchantBodyHtml`), whose
 * bridging rail lays one rank per click. The scroll picked is `store.enchant` with `helper` set, so the Enchant
 * dialog's choices, commands and request flow work the same here; nothing new runs on the server.
 */
import type { EnchantCandidateView, EnchantHelperGearView, EnchantHelperSlotView, PlayerView } from '../shared/view.ts';
import { refAttr } from './rules.ts';
import { store } from './store.ts';
import { degreeOdds, emporiumButton, enchantBodyHtml, esc, helperScroll, keepScroll, setHtml, signed, SLOT_NAMES } from './ui.ts';

const EQUIPPED: Record<NonNullable<EnchantHelperGearView['equipped']>, string> = { main: 'Main hand', off: 'Off hand', both: 'Both hands', worn: 'Worn' };

/** "a weapon's prefix", "armor's suffix". */
function slotPhrase(kind: EnchantHelperGearView['kind'], slot: 'prefix' | 'suffix'): string {
  return `${kind === 'armor' ? 'armor' : `a ${kind}`}'s ${slot}`;
}

/** A slot's enchantment as a tag with its rules on hover, or "—" for an empty slot. */
function currentTag(s: EnchantHelperSlotView): string {
  const c = s.current;
  return c ? `<span class="tag ench ref"${refAttr(c.ref, `${SLOT_NAMES[s.slot]}, rank ${c.rank}: ${c.text}`)}>${esc(c.name)} ${esc(c.rank)}</span>` : '<span class="muted">—</span>';
}

/** One step back: off the scroll, then the slot, then the gear, then the helper closes. */
export function helperBack(): void {
  const h = store.enchantHelper;
  if (!h) return;
  if (store.enchant?.helper) store.enchant = null;
  else if (h.slot) store.enchantHelper = { gearUid: h.gearUid };
  else if (h.gearUid) store.enchantHelper = {};
  else store.enchantHelper = null;
}

/** The helper's box: a breadcrumb, then the gear, slot, scroll and detail panes (only the deepest on a narrow screen). */
export function enchantHelperHtml(view: PlayerView): string {
  const helper = view.hub?.enchanting;
  const h = store.enchantHelper ?? {};
  if (!helper) return '';
  const items = view.sheet?.items ?? [];
  const gear = helper.gear.find((g) => g.uid === h.gearUid);
  const slot = gear && h.slot ? gear[h.slot] : undefined;
  // The scroll picked here (the Enchant dialog's pick, drawn in this helper for this gear's slot).
  const pick = store.enchant;
  const st = pick?.helper && gear && slot && pick.helper.gearUid === gear.uid && pick.helper.slot === slot.slot ? pick : null;
  const scroll = st && slot ? slot.scrolls.find((s) => s.uid === st.itemUid) : undefined;
  const depth = scroll ? 3 : slot ? 2 : gear ? 1 : 0;
  const pane = (n: number, cls: string, title: string, body: string) =>
    `<section class="eh-pane ${cls}${n === depth ? ' deepest' : ''}" aria-label="${esc(title)}"><h2>${esc(title)}</h2>${body}</section>`;
  const hint = (text: string) => `<p class="muted eh-hint">${esc(text)}</p>`;

  // Gear: equipped first, then the rest you carry.
  const gearRow = (g: EnchantHelperGearView) => {
    const item = items.find((i) => i.uid === g.uid);
    const d = item?.durability;
    return `<button type="button" class="eh-gear-row${g === gear ? ' chosen' : ''}" data-ench-gear="${esc(g.uid)}" aria-pressed="${g === gear}">`
      + `<span class="eh-gear-name"><b>${esc(g.name)}</b>${g.equipped ? ` <span class="tag">${EQUIPPED[g.equipped]}</span>` : ''}`
      + `${item?.blessed ? ' <span class="tag blessed">blessed</span>' : ''}${d?.broken ? ' <span class="tag broken">broken</span>' : ''}</span>`
      + `<span class="eh-gear-slots">${currentTag(g.prefix)} ${currentTag(g.suffix)}${d ? ` <span class="muted">${d.hp}/${d.max} HP</span>` : ''}</span></button>`;
  };
  const carried = helper.gear.findIndex((g) => !g.equipped);
  const gearList = helper.gear.map((g, i) => `${i === carried ? '<div class="gear-caption">Carried</div>' : ''}${gearRow(g)}`).join('')
    || hint('You carry no weapons, shields or armor.');

  // Slots: what fills each, how many scrolls fit, and the Emporium's raise and extract.
  let slots = hint('Choose a weapon, shield or armor.');
  if (gear) {
    const card = (s: EnchantHelperSlotView) => {
      const now = s.scrolls.filter((x) => !x.bridge.length).length;
      const offer = view.hub?.emporium.find((o) => o.itemUid === gear.uid && o.slot === s.slot);
      const actions = offer
        ? `${offer.increment ? emporiumButton(offer, 'increment', `Raise to rank ${offer.increment.toRank}`, offer.increment.fee, offer.increment.blocked) : ''}`
          + emporiumButton(offer, 'extract', 'Extract', offer.extract.fee, offer.extract.blocked)
        : '';
      const on = s === slot;
      return `<div class="eh-slot-card${on ? ' chosen' : ''}">`
        + `<button type="button" class="eh-slot-pick" data-ench-slot="${s.slot}" aria-pressed="${on}"><span class="lbl">${SLOT_NAMES[s.slot]}</span>`
        + `<span class="eh-slot-now">${s.current ? `${currentTag(s)} <span class="muted">${esc(s.current.text)}</span>` : '<span class="muted">Empty</span>'}</span>`
        + `<span class="muted">${s.scrolls.length} ${s.scrolls.length === 1 ? 'scroll fits' : 'scrolls fit'} · ${now} ${now === 1 ? 'goes' : 'go'} on now</span></button>`
        + `${actions ? `<div class="eh-slot-actions">${actions}</div>` : ''}</div>`;
    };
    const runes = (items.find((i) => i.uid === gear.uid)?.enchantments ?? []).filter((e) => e.rune);
    slots = card(gear.prefix) + card(gear.suffix)
      + (runes.length ? `<p class="muted eh-runes">Runes: ${runes.map((e) => `<span class="ref"${refAttr(e.ref, `${SLOT_NAMES[e.slot]}: ${e.text}`)}>${esc(e.name)}</span>`).join(', ')} <span class="muted">(Etch runes from your pack)</span></p>` : '');
  }

  // Scrolls: every one in your pack and vault that fits the slot, with its check and its bridging.
  let scrolls = hint('Choose its prefix or suffix.');
  if (gear && slot) {
    const cur = slot.current;
    const row = (s: EnchantCandidateView) => {
      const odds = degreeOdds(s.modifier, s.dc);
      const steps = s.bridge.length;
      const have = s.bridge.filter((b) => slot.scrolls.some((o) => o.rankIndex === b.rankIndex && !o.blocked)).length;
      const bridging = !steps ? '<span class="good">goes on now</span>'
        : `<span class="${have === steps ? 'good' : 'bad'}">bridge ${steps} ${steps === 1 ? 'rank' : 'ranks'} · have ${have} of ${steps}</span>`;
      const note = cur && cur.line === s.line && cur.rankIndex === s.rankIndex ? ' <span class="muted">same as now</span>'
        : cur && s.rankIndex < cur.rankIndex ? ` <span class="bad">weaker than ${esc(cur.name)} ${esc(cur.rank)}</span>` : '';
      const on = s === scroll;
      return `<button type="button" class="eh-scroll-row${on ? ' chosen' : ''}" data-ench-pick="${esc(s.uid)}" aria-pressed="${on}">`
        + `<span class="eh-scroll-head"><b class="ref"${refAttr(s.ref)}>${esc(s.name)}</b> <span class="ench-rank">Rank ${esc(s.rank)}</span> <span class="tag">${s.where}</span>`
        + `${s.qty > 1 ? ` <span class="muted">×${s.qty}</span>` : ''}${s.damaged ? ' <span class="tag damaged">damaged</span>' : ''}</span>`
        + `<span class="eh-scroll-text">${esc(s.text)}</span>`
        + `<span class="eh-scroll-check">${esc(s.skill)} ${signed(s.modifier)} vs DC ${s.dc} · ${odds[2] + odds[3]}%</span>`
        + `<span class="eh-scroll-bridge">${bridging}${note}</span></button>`;
    };
    scrolls = slot.scrolls.map(row).join('') || hint(`No enchant scroll in your pack or vault fits ${slotPhrase(gear.kind, slot.slot)}.`);
  }

  // The scroll itself: the Enchant dialog's body (the bridging rail, or the check), for this gear.
  let detail = hint('Choose a scroll to see its check and the ranks it must bridge.');
  const e = st?.helper && scroll ? helperScroll(view, st.helper, scroll.uid) : undefined;
  if (st && e && scroll) {
    detail = `<div class="eh-detail-head"><b class="ref"${refAttr(e.ref)}>${esc(e.name)}</b> <span class="ench-rank">Rank ${esc(e.rank)}</span>`
      + `${e.damaged ? ' <span class="tag damaged" title="It survived one failure: another failure destroys it">damaged</span>' : ''}</div>
      <p><span class="tag">${e.slot}</span> ${esc(e.text)}</p>
      <p class="muted">In town, this takes no time at all.${scroll.where === 'vault' ? ' It is read straight from your vault.' : ''}</p>
      ${enchantBodyHtml(view, st, e, e.targets[0])}`;
  }

  const crumbs = ['Gear', gear?.name, slot ? SLOT_NAMES[slot.slot] : undefined, scroll ? `${scroll.name} ${scroll.rank}` : undefined]
    .filter((x): x is string => !!x).map((x) => `<span>${esc(x)}</span>`).join('<span class="muted" aria-hidden="true">›</span>');
  return `
    <div class="modal-box ench-box ench-helper-box" role="dialog" aria-label="Enchanting">
      <div class="eh-head">
        <h1>Enchanting</h1>
        <nav class="eh-crumbs" aria-label="Where you are">${crumbs}</nav>
        <span class="spacer"></span>
        ${depth ? '<button type="button" class="small" data-ench-helper="back" title="Back (Esc)">‹ Back</button>' : ''}
        <button type="button" class="small" data-ench-helper="close">Close</button>
      </div>
      <p class="muted">Pick a piece of gear, then its prefix or suffix, then a scroll from your pack or vault. Enchanting powder and holy water must be in your pack.</p>
      <div class="eh-panes">
        ${pane(0, 'eh-gear', 'Gear', gearList)}
        ${pane(1, 'eh-slots', gear ? gear.name : 'Slots', slots)}
        ${pane(2, 'eh-scrolls', slot ? `${SLOT_NAMES[slot.slot]} scrolls` : 'Scrolls', scrolls)}
        ${pane(3, 'eh-detail', 'Enchant', detail)}
      </div>
    </div>`;
}

/**
 * The helper while it's open in town. A pick that went stale falls back a step: gear that's gone clears the gear and
 * slot, and a scroll the helper no longer lists (laid, destroyed) drops back to its slot, which shows what's on now.
 */
export function renderEnchantHelper(view: PlayerView): void {
  const el = document.getElementById('enchhelp')!;
  const helper = view.phase === 'hub' ? view.hub?.enchanting : null;
  if (!store.enchantHelper || !helper) {
    store.enchantHelper = null;
    if (store.enchant?.helper) store.enchant = null;
    el.classList.add('hidden');
    return setHtml(el, '');
  }
  const h = store.enchantHelper;
  if (h.gearUid && !helper.gear.some((g) => g.uid === h.gearUid)) store.enchantHelper = {};
  const st = store.enchant;
  const at = store.enchantHelper;
  if (st?.helper && (st.helper.gearUid !== at.gearUid || st.helper.slot !== at.slot || !helperScroll(view, st.helper, st.itemUid))) store.enchant = null;
  el.classList.remove('hidden');
  keepScroll(el, ['.ench-helper-box', '.eh-gear', '.eh-scrolls', '.eh-detail'], () => setHtml(el, enchantHelperHtml(view)));
}
